// NT-2 ("Per-type toggles and quiet hours") — this app's first Settings > Notifications
// section (SettingsLayout.tsx previously omitted it as "not yet built"). Six per-category
// toggles mirror reminders/dispatch.ts's own `NOTIFICATION_TYPE_SETTINGS_KEY` map 1:1 —
// `ad_hoc`/`calendar_event` reminders have no toggle here since the owner already controls
// each one directly (cancel a specific reminder; set a calendar's own lead time to 0), a
// coarse category switch would just be a second, redundant control for those. Quiet hours
// (§16.12, default 23:00–08:00) already worked server-side since WP-2.7 but had no UI until now.
import { useEffect, useState } from 'react';
import { checkboxClass, inputClass, primaryButtonInlineClass } from '../../components/FormKit';
import { useSettings, useSetSetting } from '../../lib/settings';

const DEFAULT_QUIET_HOURS_START = 23;
const DEFAULT_QUIET_HOURS_END = 8;
const DEFAULT_MORNING_AGENDA_HOUR = 8; // WP-C1.9 (AVQ-4) — matches reminders/dispatch.ts's own default

// Matches reminders/dispatch.ts's own NOTIFICATION_TYPE_SETTINGS_KEY exactly, one row per key.
const NOTIFICATION_TYPES = [
  { key: 'notifyPayableDue', label: 'Payable due' },
  { key: 'notifyPayableOverdue', label: 'Payable overdue' },
  { key: 'notifyScheduleDue', label: 'Schedule item due' },
  { key: 'notifyScheduleOverdue', label: 'Schedule item overdue' },
  { key: 'notifyRecurringConfirm', label: 'Recurring confirm-needed' },
  { key: 'notifyGoalMilestone', label: 'Goal milestone reached' },
] as const;

function isEnabled(settings: Record<string, unknown> | undefined, key: string): boolean {
  return settings?.[key] !== false; // absent or anything but literal false -> enabled
}

export function SettingsNotifications(): React.JSX.Element {
  const { data: settings } = useSettings();
  const setSetting = useSetSetting();
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [quietStart, setQuietStart] = useState(String(DEFAULT_QUIET_HOURS_START));
  const [quietEnd, setQuietEnd] = useState(String(DEFAULT_QUIET_HOURS_END));
  const [morningAgendaEnabled, setMorningAgendaEnabled] = useState(false);
  const [morningAgendaHour, setMorningAgendaHour] = useState(String(DEFAULT_MORNING_AGENDA_HOUR));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings === undefined) return;
    setEnabled(
      Object.fromEntries(NOTIFICATION_TYPES.map(({ key }) => [key, isEnabled(settings, key)])),
    );
    if (typeof settings.quietHoursStart === 'number')
      setQuietStart(String(settings.quietHoursStart));
    if (typeof settings.quietHoursEnd === 'number') setQuietEnd(String(settings.quietHoursEnd));
    setMorningAgendaEnabled(settings.morningAgendaEnabled === true);
    if (typeof settings.morningAgendaHour === 'number')
      setMorningAgendaHour(String(settings.morningAgendaHour));
  }, [settings]);

  const save = (): void => {
    setSaved(false);
    for (const { key } of NOTIFICATION_TYPES) {
      setSetting.mutate({ key, value: enabled[key] ?? true });
    }
    const start = Number(quietStart);
    if (Number.isInteger(start) && start >= 0 && start <= 23) {
      setSetting.mutate({ key: 'quietHoursStart', value: start });
    }
    const end = Number(quietEnd);
    if (Number.isInteger(end) && end >= 0 && end <= 23) {
      setSetting.mutate({ key: 'quietHoursEnd', value: end });
    }
    setSetting.mutate({ key: 'morningAgendaEnabled', value: morningAgendaEnabled });
    const agendaHour = Number(morningAgendaHour);
    if (Number.isInteger(agendaHour) && agendaHour >= 0 && agendaHour <= 23) {
      setSetting.mutate({ key: 'morningAgendaHour', value: agendaHour });
    }
    setSaved(true);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Notification types (NT-2)</h2>
        <p className="mb-3 text-xs text-muted">
          The assistant sends these over WhatsApp per the §16.11 notification matrix. Turn any off
          you don't want pushed — they still appear in the app itself.
        </p>
        <div className="space-y-2">
          {NOTIFICATION_TYPES.map((t) => (
            <label key={t.key} className="flex items-center gap-1.5 text-sm text-ink">
              <input
                type="checkbox"
                className={checkboxClass}
                checked={enabled[t.key] ?? true}
                onChange={(e) => {
                  setEnabled((prev) => ({ ...prev, [t.key]: e.target.checked }));
                }}
              />
              {t.label}
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Quiet hours (§16.12)</h2>
        <p className="mb-3 text-xs text-muted">
          No WhatsApp pushes land during this window — a due reminder is held and sent once quiet
          hours end, never dropped. Infra alerts (backup failed, bridge down) go by email instead
          and are exempt.
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="text-sm text-ink" htmlFor="notif-quiet-start">
            Quiet hours start
            <input
              id="notif-quiet-start"
              type="number"
              min={0}
              max={23}
              className={`${inputClass} mt-1 w-24`}
              value={quietStart}
              onChange={(e) => {
                setQuietStart(e.target.value);
              }}
            />
          </label>
          <label className="text-sm text-ink" htmlFor="notif-quiet-end">
            Quiet hours end
            <input
              id="notif-quiet-end"
              type="number"
              min={0}
              max={23}
              className={`${inputClass} mt-1 w-24`}
              value={quietEnd}
              onChange={(e) => {
                setQuietEnd(e.target.value);
              }}
            />
          </label>
        </div>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Morning agenda (AVQ-4)</h2>
        <p className="mb-3 text-xs text-muted">
          Opt in for a daily WhatsApp digest at a set time: today's calendar agenda plus anything
          due today. Off by default.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-ink">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={morningAgendaEnabled}
              onChange={(e) => {
                setMorningAgendaEnabled(e.target.checked);
              }}
            />
            Morning agenda
          </label>
          <label className="text-sm text-ink" htmlFor="notif-agenda-hour">
            Agenda time (Karachi hour, 0–23)
            <input
              id="notif-agenda-hour"
              type="number"
              min={0}
              max={23}
              className={`${inputClass} mt-1 w-24`}
              value={morningAgendaHour}
              onChange={(e) => {
                setMorningAgendaHour(e.target.value);
              }}
            />
          </label>
        </div>
      </section>

      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={setSetting.isPending}
        onClick={save}
      >
        Save
      </button>
      {saved && <span className="ml-2 text-sm text-success">Saved</span>}
    </div>
  );
}
